import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { environment } from '../../../../environments/environment'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { Block, Book, Column, KeyBitArray, KeySystem, Library, Page, Structure } from '../../../models/dev.test.models'
import { KeyingLibraryService } from '../../../services/keying.library.service'
import { slideInDown } from '../../../animations/slideInDown'
import { fadeInOnly } from '../../../animations/fadeInOnly'
import { slideInOnlyDown } from '../../../animations/slideInOnlyDown'
import { slideInOnlyUpStaggered } from '../../../animations/slideInOnlyUpStaggered'
import { MasterKey, KeywayService } from '@overtur-monorepo/keying-hierarchy'
import { Assignment } from 'libs/keying-hierarchy/src/lib/models/keying-models'
import { AssignmentService } from '../../../services/assignment.service'
import { ReserveDialogComponent } from '../../../dialogs/reserve-dialog/reserve-dialog.component'
import { DialogService } from '../../../services/dialog.service'
import { LibraryItemData } from '../library-list-item/library-list-item.component'
import { KeyNodeType, KeywayType } from '@overtur-monorepo/keying-shared'
import { KeyingBookService } from '../../../services/keying.book.service'
import { ErrorService } from '../../../services/error.service'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { LoggingService } from '../../../services/logging.service'
import { LoadingService } from '../../../services/loading.service'
import { MatCheckboxChange } from '@angular/material/checkbox'

@Component({
  selector: 'app-key-bitting-library',
  templateUrl: './key-bitting-library.component.html',
  styleUrls: ['./key-bitting-library.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeIn, slideInDown, fadeInOnly, slideInOnlyDown, slideInOnlyUpStaggered],
  standalone: false,
})
export class KeyBittingLibraryComponent implements OnInit, OnDestroy, OnChanges {
  destroy$ = new Subject()
  canMinimize = environment.flags.canMinimize

  selectedKbaArray = []
  connectedTo = []

  killSearch = true
  dragging: MasterKey = null
  noOfBlocks
  allKeyWaysFromFamily

  @Input() selectedBook: Book
  @Input() selectedMasterKey: MasterKey
  @Input() selectedFilterKey
  @Input() assignedMasterKeyBittings
  @Input() reservationTypes
  @Input() library: Library
  @Input() keySystem: KeySystem
  @Input() structure: Structure

  @Output() selectedKbas = new EventEmitter<any>()
  @Output() updateAssignedMasterKeyBittings = new EventEmitter<any>()
  @Output() splitMasterKeyBitting = new EventEmitter<any>()
  @Output() resetBook = new EventEmitter<any>()
  @Output() selectedForMoveChanged = new EventEmitter<KeyBitArray[]>()

  keyNodeType = KeyNodeType
  keywayType = KeywayType

  sortedBooks

  combineError = 'Bittings already assigned, combined, or split cannot be used with the Combine feature.'

  isControlKeysMasterAssignedBitting = false

  // Array to track bittings selected for moving
  selectedForMoveBittings: KeyBitArray[] = []

  constructor(
    private keyingLibraryService: KeyingLibraryService,
    private assignmentService: AssignmentService,
    private dialogService: DialogService,
    private keyingBookService: KeyingBookService,
    private errorService: ErrorService,
    private loggingService: LoggingService,
    private keywayService: KeywayService,
    private loadingService: LoadingService
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    // KeyWays
    this.keywayService.keyways$.pipe(takeUntil(this.destroy$)).subscribe((keyways) => {
      this.allKeyWaysFromFamily = keyways
    })
    if (this.library) {
      this.sortLibrary()
    }
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.library && this.library) {
      this.sortLibrary()
    }
    if (changes.selectedBook) {
      this.selectedBook = changes.selectedBook.currentValue

      /**
       * Clear local selections when book changes to keep parent and child in sync.
       * Both hierarchy.component (parent) and this component maintain selectedKbaArray.
       * Without this sync, checkboxes may appear selected when parent thinks nothing is selected,
       * causing unassign operations to behave unexpectedly.
       * See: docs/features/DOUBLE_CLICK_KEY_ASSIGNMENT.md
       */
      this.clearSelections()
      this.selectedKbas.emit(this.selectedKbaArray)

      if (this.selectedBook) {
        this.connectedTo.push('keysList')
        for (let page of this.selectedBook.pages) {
          for (let col of page.columns) {
            for (let block of col.blocks) {
              this.connectedTo.push(block.id)
            }
          }
        }
      } else {
        this.connectedTo = []
        this.resetLocalChanges()
      }

      let assigned = this.assignedMasterKeyBittings.filter((a) => a.masterKeyId != null)
      assigned.forEach((a) => {
        let key = this.selectedMasterKey.children.find((k) => k.id == a.masterKeyId)
        key.isAssignedLocally = true
        key.unassignedLocally = false
      })

      // TODO: need to update phx so that I can call it to clear the search input.
      this.killSearch = true
      setTimeout(() => {
        this.killSearch = false
      })
    }

    if (changes.selectedMasterKey) {
      this.loggingService.log('selected Master Key:', changes.selectedMasterKey)
      this.loggingService.log('Assigned Bittings:', this.assignedMasterKeyBittings)

      // Clear local selections when master key changes - see comment in selectedBook block above
      this.clearSelections()
      this.selectedKbas.emit(this.selectedKbaArray)

      let assigned = this.assignedMasterKeyBittings.filter((a) => a.masterKeyId != null)
      this.loggingService.log('assigned', assigned)
      assigned.forEach((a) => {
        let key = this.selectedMasterKey.children.find((k) => k.id == a.masterKeyId)
        key.isAssignedLocally = true
        key.unassignedLocally = false
      })
    }



    if (this.structure && this.structure.combinatingRuleSetId === 5) {
      this.noOfBlocks = 6
    } else if (this.structure && this.structure.combinatingRuleSetId === 7) {
      this.noOfBlocks = 5
    } else {
      this.noOfBlocks = 4
    }
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  sortLibrary() {
    if (!this.library?.books) return
    this.sortedBooks = [...this.library.books].sort((a, b) => a.keySectionName.localeCompare(b.keySectionName))
  }

  /* istanbul ignore next */
  filterKeyList(crossKey) {
    this.selectedFilterKey = crossKey

    this.selectedMasterKey.children.map((child) => {
      let key = crossKey.children.find((ckc) => child.id === ckc.id)

      if (key) {
        child.inFilter = true
      } else {
        child.inFilter = false
      }
    })
  }

  /* istanbul ignore next */
  clearFilter() {
    this.selectedFilterKey = new MasterKey()
    this.selectedMasterKey.children.map((child) => {
      child.inFilter = true
    })
  }

  /* istanbul ignore next */
  convertToListItem(book) {
    let newLibraryItemData = new LibraryItemData()
    const bookLabelWithNumber = `Book ${book.bookNumber}`
    newLibraryItemData.columns.push(
      { label: bookLabelWithNumber, data: book.assignmentString },
      { label: 'Key Section', data: book.keySectionName },
      { label: 'ID', data: book.id },
      { label: 'Total', data: book.stats.total },
      { label: 'Assigned', data: book.stats.assigned },
      { label: 'Available', data: book.stats.available }
    )
    return newLibraryItemData
  }

  /* istanbul ignore next */
  sortHierarchically(subject, flat) {
    let mapped = flat.map((s) => s.keySetSymbol)

    subject = subject.sort((a, b) => {
      return mapped.indexOf(a.keySetSymbol) - mapped.indexOf(b.keySetSymbol)
    })

    return subject
  }

  /* istanbul ignore next */
  dropUnassignedList(event) {
    let previousKBA: KeyBitArray = event.previousContainer.data[event.previousIndex]
    let copiedKba: KeyBitArray = Object.assign({}, previousKBA)

    if (copiedKba?.assignment?.masterKey?.id) {
      let key = this.selectedMasterKey.children.find((key) => key.id === copiedKba.assignment.masterKey.id)
      key.unassignedLocally = true
      key.assignment = new Assignment()
      previousKBA.assignment = new Assignment()

      let data = {
        masterkeys: this.selectedMasterKey.children,
        selectedMasterKey: this.selectedMasterKey,
        selectedBook: this.selectedBook,
        assignedMasterKeyBittings: this.assignedMasterKeyBittings,
        reservationTypes: this.reservationTypes,
      }

      this.updateAssignedMasterKeyBittings.emit(this.assignmentService.prepareAssignmentRequest(data))
    }

    this.dragging = null
  }

  /* istanbul ignore next */
  dropInBook(event) {
    // this.loggingService.log('event.previousContainer.data', event.previousContainer.data)
    // this.loggingService.log('event.container.data', event.container.data)
    // this.loggingService.log('event.previousIndex', event.previousIndex)
    // this.loggingService.log('event.currentIndex', event.currentIndex)

    const droppedKey: MasterKey = event.item.data

    let previousKBA: KeyBitArray = event.previousContainer.data[event.previousIndex]
    let newKba = event.container.data[event.currentIndex]
    if (event.currentIndex >= event.container.data.length) {
      newKba = event.container.data[event.currentIndex - 1]
    }
    if (this.canAssign(newKba)) {
      let copiedNewKba: KeyBitArray = Object.assign({}, newKba)

      if (droppedKey) {
        droppedKey.assignment = new Assignment()
        droppedKey.assignment.kba = newKba
        newKba.assignment = new Assignment()
        newKba.assignment.masterKey = droppedKey
      } else if (previousKBA.assignment) {
        newKba.assignment = previousKBA.assignment
        previousKBA.assignment = copiedNewKba.assignment
      }

      let data = {
        masterkeys: this.selectedMasterKey.children,
        selectedMasterKey: this.selectedMasterKey,
        selectedBook: this.selectedBook,
        assignedMasterKeyBittings: this.assignedMasterKeyBittings,
        reservationTypes: this.reservationTypes,
      }

      this.updateAssignedMasterKeyBittings.emit(this.assignmentService.prepareAssignmentRequest(data))
    }

    this.dragging = null
  }

  /* istanbul ignore next */
  dragStart(key) {
    this.dragging = key
  }

  canAssign(kba) {
    if (kba?.hidden) {
      return false
    } else if (
      kba?.hasError &&
      this.dragging?.masterKeyType?.name !== 'SingleKeyDifferentMaster' &&
      this.dragging?.masterKeyType?.name !== 'DirectChangeMaster'
    ) {
      return false
    } else if (kba?.assignment?.reserved) {
      return false
    } else if (kba?.assignment?.masterKey?.id) {
      return false
    } else if (kba?.children && kba?.children?.length > 0) {
      return false
    }
    return true
  }

  /* istanbul ignore next */
  openDialog(dialog, kba): void {
    let data = {
      masterkeys: this.selectedMasterKey.children,
      selectedKbaArray: [kba],
      selectedMasterKey: this.selectedMasterKey,
      selectedBook: this.selectedBook,
      assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      reservationTypes: this.reservationTypes,
    }

    switch (dialog) {
      case 1:
        dialog = ReserveDialogComponent
        break
      default:
        break
    }

    let dialogRef = this.dialogService.open(dialog, data, true)

    dialogRef.afterClosed().subscribe((request) => {
      if (request) {
        switch (dialog) {
          case ReserveDialogComponent:
            this.updateAssignedMasterKeyBittings.emit(this.assignmentService.prepareAssignmentRequest(data))
            break
          default:
            break
        }
      }
    })
  }

  /* istanbul ignore next */
  unassignKbas(kba) {
    let data = {
      masterkeys: this.selectedMasterKey.children,
      selectedKbaArray: [kba],
      selectedMasterKey: this.selectedMasterKey,
      selectedBook: this.selectedBook,
      assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      rules: {
        andReserved: true,
      },
    }

    this.assignmentService.unassignKbas(data)
    this.updateAssignedMasterKeyBittings.emit(this.assignmentService.prepareAssignmentRequest(data))
    this.selectedMasterKey.children.forEach((child) => {
      if (child.inFilter || (child.unassignedLocally && !child.isAssignedLocally && !child.isFutureKey)) {
        child.inFilter = true
      } else {
        child.inFilter = false
      }
    })
  }

  /* istanbul ignore next */
  assignKbas(kba, skipGuidelineViolators = true) {
    let data = {
      masterkeys: this.selectedMasterKey.children,
      selectedKbaArray: [kba],
      selectedMasterKey: this.selectedMasterKey,
      selectedBook: this.selectedBook,
      assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      rules: {
        skipGuidelineViolators: skipGuidelineViolators,
      },
    }

    this.assignmentService.assignKbas(data)
    this.updateAssignedMasterKeyBittings.emit(this.assignmentService.prepareAssignmentRequest(data))
  }

  /* istanbul ignore next */
  splitBitting(kba) {
    let data = {
      bitting: kba.flatBits(),
    }
    this.loadingService.set({ isLoading: true, message: 'Splitting' })
    this.keyingBookService
      .bittingSplit(this.keySystem.id, this.selectedMasterKey.id, this.selectedBook.id, data)
      .pipe(take(1))
      .subscribe(
        () => {
          this.splitMasterKeyBitting.emit(data)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  undoSplitBitting(kba) {
    let data = {
      bitting: kba.flatBits(),
    }

    kba.children.forEach((splitCombination) => {
      let bittingToRemove = this.assignedMasterKeyBittings.find(
        (requestedAssignment) => requestedAssignment.bitting === splitCombination.masterKeyBitting
      )

      if (bittingToRemove) {
        this.loggingService.log('Removing bitting from assignment queue:', bittingToRemove)

        let assignedKey = this.selectedMasterKey.children.find((child) => child.id === bittingToRemove.masterKeyId)

        if (assignedKey) {
          assignedKey.isAssignedLocally = false
          assignedKey.unassignedLocally = true
          assignedKey.assignment = new Assignment()
        }

        this.assignedMasterKeyBittings.forEach((element, index) => {
          if (bittingToRemove.bitting === element.bitting) {
            this.assignedMasterKeyBittings.splice(index, 1)
          }
        })
      }
    })

    this.loadingService.set({ isLoading: true, message: 'Merging' })
    this.keyingBookService
      .undoBittingSplit(this.keySystem.id, this.selectedMasterKey.id, this.selectedBook.id, data)
      .pipe(take(1))
      .subscribe(
        () => {
          this.splitMasterKeyBitting.emit(data)
          this.loadingService.set({ isLoading: true, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: true, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  mergeSplit(kba) {
    this.undoSplitBitting(kba)
  }

  /* istanbul ignore next */
  mergeSplitConfirm(kba) {
    let data = {
      title: 'Merge Split',
      message: `Are you sure you want to merge split bittings?`,
      cancel: 'Cancel',
      confirm: 'Merge',
      showCancel: true,
      reasonRequired: false,
    }

    if (this.keySystem.isPublished) {
      data.message = 'This cylinder has been published. Are you sure you want to delete?'
      data.reasonRequired = true
    }

    let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.undoSplitBitting(kba)
      }
    })
  }

  /* istanbul ignore next */
  filter(on) {
    this.deselectKbas()
    this.selectedBook.pages.forEach((page) => {
      page.columns.forEach((column) => {
        column.blocks.forEach((block) => {
          block.kbas.forEach((kba) => {
            let kbaString = ''
            let xString = ''
            const onSplit = on.split('')

            onSplit.forEach((on) => {
              xString = xString + 'x'
            })

            kba.bits.forEach((bit) => {
              bit.inFilter = false
              kbaString = kbaString + bit.value
            })

            if (!kbaString.includes(on)) {
              kba.disabled = true
              kba.show = false
            } else {
              kba.disabled = false
              kba.show = true
              kbaString = kbaString.replace(on, xString)

              const kbaStringArray = kbaString.split('')

              kbaStringArray.forEach((bit, bitIndex) => {
                if (bit === 'x') {
                  kba.bits[bitIndex].inFilter = true
                }
              })
            }
          })
        })
      })
    })
  }

  manageSelectedKbaArray() {
    this.selectedKbaArray.forEach((k: KeyBitArray, index) => {
      if (!k.selected) {
        delete this.selectedKbaArray[index]
      }
    })
    this.selectedKbaArray = this.selectedKbaArray.filter((el) => {
      return el != null
    })
    this.selectedKbas.emit(this.selectedKbaArray)
  }

  deselectKbas() {
    this.selectedKbaArray.forEach((kba: KeyBitArray) => {
      kba.selected = false
      if (kba.parent.selected) {
        this.deselectParent(kba)
      }
    })
    this.selectedKbaArray = []
  }

  deselectParent(item) {
    if (item.parent && item.parent.selected) {
      item.parent.selected = false
      this.deselectParent(item.parent)
    }
  }

  /* istanbul ignore next */
  selectKba(event: MatCheckboxChange, kba: any, page: any, column: any, block: any) {
    if (kba.disabled) {
      return
    }

    // ensure model is a boolean
    kba.selected = event.checked

    if (event.checked) {
      // add if not already present in array
      if (!this.selectedKbaArray.includes(kba)) {
        this.selectedKbaArray.push(kba)
      }
      // Also add to move bittings selection if eligible
      this.addToMoveBittingsIfEligible(kba)
    } else {
      // remove when unchecked
      const idx = this.selectedKbaArray.indexOf(kba)
      if (idx !== -1) {
        this.selectedKbaArray.splice(idx, 1)
      }
      // Also remove from move bittings selection
      this.removeFromMoveBittings(kba)
    }

    this.manageSelectedKbaArray()
    this.selectedForMoveChanged.emit([...this.selectedForMoveBittings])

    // If intent is to clear other selections when selecting this, do that
    // after updating the kba selection above.
    page.selected = false
    column.selected = false
    block.selected = false
  }

  /* istanbul ignore next */
  selectBlock(event: MatCheckboxChange, block: any, column: any, page: any) {
    if (block.isMinimized) {
      return
    }

    const isChecked = event.checked

    block.selected = isChecked
    block.anyFound = false

    block.kbas.forEach((kba: KeyBitArray) => {
      if (!kba.disabled && !kba.hidden && !kba.children) {
        kba.selected = isChecked
        block.anyFound = true

        if (isChecked) {
          // add if not already present
          if (!this.selectedKbaArray.includes(kba)) {
            this.selectedKbaArray.push(kba)
          }
          this.addToMoveBittingsIfEligible(kba)
        } else {
          // remove if unchecked
          const idx = this.selectedKbaArray.indexOf(kba)
          if (idx !== -1) {
            this.selectedKbaArray.splice(idx, 1)
          }
          this.removeFromMoveBittings(kba)
        }
      }
    })

    this.manageSelectedKbaArray()
    this.selectedForMoveChanged.emit([...this.selectedForMoveBittings])

    if (!block.selected) {
      page.selected = false
      column.selected = false
    }

    if (!block.anyFound) {
      block.selected = false
      this.deselectParent(block.parent)
    }
  }

  /* istanbul ignore next */
  selectColumn(event: MatCheckboxChange, column: any, page: any) {
    if (column.isMinimized) {
      return
    }

    const isChecked = event.checked
    column.selected = isChecked

    column.blocks.forEach((block: any) => {
      this.selectBlock(event, block, column, page)
    })
  }

  selectPage(event, page) {
    page.selected = event
    page.columns.forEach((column) => {
      this.selectColumn(event, column, page)
    })
  }

  selectBook(book) {
    const keySection = this.allKeyWaysFromFamily.find((kwy) => kwy.id === book.keywayId)
    this.keyingLibraryService.emitKeySection(keySection)
    this.keyingLibraryService.selectBook(book)
  }

  /* istanbul ignore next */
  resetLocalChanges() {
    this.deselectKbas()
  }

  clearSelections() {
    this.selectedKbaArray.map((kba) => {
      kba.selected = false
      this.deselectParent(kba)
    })
    this.selectedKbaArray = []
    // Also clear move bittings selection and notify parent
    this.selectedForMoveBittings = []
    this.selectedForMoveChanged.emit([...this.selectedForMoveBittings])
  }

  /* istanbul ignore next */
  separate(request) {
    this.loadingService.set({ isLoading: true, message: 'Separating' })
    this.keyingBookService
      .undoBittingsCombine(this.keySystem.id, this.selectedMasterKey.id, this.selectedBook.id, request)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.resetBook.emit()
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )

    this.clearSelections()
  }

  /* istanbul ignore next */
  combine(request) {
    this.loadingService.set({ isLoading: true, message: 'Combining' })
    this.keyingBookService
      .bittingsCombine(this.keySystem.id, this.selectedMasterKey.id, this.selectedBook.id, request)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.resetBook.emit()
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )

    this.clearSelections()
  }

  /* istanbul ignore next */
  combinePage(page: Page) {
    let combBittings = []
    page.columns.forEach((column: Column) => {
      column.blocks.forEach((block: Block) => {
        block.kbas.forEach((kba: KeyBitArray) => {
          if (!kba.isASplit && !kba.isCombine) {
            combBittings.push(kba.masterKeyBitting)
          }
        })
      })
    })

    let request = [
      {
        CombineType: 3,
        Bittings: combBittings,
      },
    ]

    this.loggingService.log('request', request)

    this.combine(request)
  }

  /* istanbul ignore next */
  previewCombinePage(page: Page) {
    page.columns.forEach((column: Column) => {
      column.blocks.forEach((block: Block) => {
        block.kbas.forEach((kba: KeyBitArray) => {
          let uri = kba
          this.previewCheck(uri)
        })
      })
    })
  }

  /* istanbul ignore next */
  separatePage(page) {
    let request = {
      bitting: page.undoCombination,
    }
    this.loggingService.log('request', request)
    this.separate(request)
  }

  /* istanbul ignore next */
  combineColumn(column: Column) {
    let combBittings = []

    column.blocks.forEach((block: Block) => {
      block.kbas.forEach((kba: KeyBitArray) => {
        if (!kba.isASplit && !kba.isCombine) {
          combBittings.push(kba.masterKeyBitting)
        }
      })
    })

    let request = [
      {
        CombineType: 2,
        Bittings: combBittings,
      },
    ]

    this.loggingService.log('request', request)

    this.combine(request)
  }

  /* istanbul ignore next */
  previewCombineColumn(column: Column) {
    column.blocks.forEach((block: Block) => {
      block.kbas.forEach((kba: KeyBitArray) => {
        let uri = kba
        this.previewCheck(uri)
      })
    })
  }

  /* istanbul ignore next */
  separateColumn(column) {
    let request = {
      bitting: column.undoCombination,
    }
    this.loggingService.log('request', request)
    this.separate(request)
  }

  /* istanbul ignore next */
  combineBlock(block) {
    let request = [
      {
        CombineType: 1,
        Bittings: block.bittingsArray,
      },
    ]
    this.combine(request)
  }

  /* istanbul ignore next */
  previewCombineBlock(block) {
    block.kbas.forEach((kba: KeyBitArray) => {
      let uri = kba
      this.previewCheck(uri)
    })
  }

  /* istanbul ignore next */
  separateBlock(block) {
    let request = {
      bitting: block.undoCombination,
    }
    this.loggingService.log('request', request)
    this.separate(request)
  }

  /* istanbul ignore next */
  combineBlocksAcross(page: Page, block: Block) {
    let combBittings = []
    let error = false

    page.columns.forEach((column: Column) => {
      column.blocks[block.number].kbas.forEach((kba) => {
        let uri = kba
        if (this.combineCheck(uri)) {
          combBittings.push(uri.masterKeyBitting)
        } else {
          error = true
        }
      })
    })

    if (!error) {
      let request = [
        {
          CombineType: 6,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewCombineBlocksAcross(page: Page, block: Block) {
    page.columns.forEach((column: Column) => {
      column.blocks[block.number].kbas.forEach((kba) => {
        let uri = kba
        this.previewCheck(uri)
      })
    })
  }

  /* istanbul ignore next */
  separateLine(kba) {
    let request = {
      bitting: kba.masterKeyBitting,
    }
    this.separate(request)
  }

  /* istanbul ignore next */
  combineKbaAcross(page: Page, block: Block, kba: KeyBitArray) {
    let combBittings = []
    let error = false

    page.columns.forEach((column: Column) => {
      let uri = column.blocks[block.number].kbas[kba.number]
      if (this.combineCheck(uri)) {
        combBittings.push(column.blocks[block.number].kbas[kba.number].masterKeyBitting)
      } else {
        error = true
      }
    })

    if (!error) {
      let request = [
        {
          CombineType: 4,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewCombineKbaAcross(page: Page, block: Block, kba: KeyBitArray) {
    page.columns.forEach((column: Column) => {
      let uri = column.blocks[block.number].kbas[kba.number]
      this.previewCheck(uri)
    })
  }

  /* istanbul ignore next */
  combineKbaVertical(column: Column, kba: KeyBitArray) {
    let combBittings = []
    let error = false

    column.blocks.forEach((block: Block) => {
      let uri = block.kbas[kba.number]
      if (this.combineCheck(uri)) {
        combBittings.push(uri.masterKeyBitting)
      } else {
        error = true
      }
    })

    if (!error) {
      let request = [
        {
          CombineType: 5,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewCombineKbaVertical(column: Column, kba: KeyBitArray) {
    column.blocks.forEach((block: Block) => {
      let uri = block.kbas[kba.number]
      this.previewCheck(uri)
    })
  }

  /* istanbul ignore next */
  combineBittingStack(book: Book, column: Column, block: Block, kba: KeyBitArray) {
    let combBittings = []
    let error = false

    book.pages.forEach((page: Page) => {
      let uri = page.columns[column.number].blocks[block.number].kbas[kba.number]
      if (this.combineCheck(uri)) {
        combBittings.push(uri.masterKeyBitting)
      } else {
        error = true
      }
    })

    if (!error) {
      let request = [
        {
          CombineType: 7,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewcombineBittingStack(book: Book, column: Column, block: Block, kba: KeyBitArray) {
    book.pages.forEach((page: Page) => {
      let uri = page.columns[column.number].blocks[block.number].kbas[kba.number]
      this.previewCheck(uri)
    })
  }

  /* istanbul ignore next */
  combineBlockStack(book: Book, column: Column, block: Block) {
    let combBittings = []
    let error = false

    book.pages.forEach((page: Page) => {
      page.columns[column.number].blocks[block.number].kbas.forEach((kba) => {
        let uri = kba
        if (this.combineCheck(uri)) {
          combBittings.push(uri.masterKeyBitting)
        } else {
          error = true
        }
      })
    })

    if (!error) {
      let request = [
        {
          CombineType: 8,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewCombineBlockStack(book: Book, column: Column, block: Block) {
    book.pages.forEach((page: Page) => {
      page.columns[column.number].blocks[block.number].kbas.forEach((kba) => {
        let uri = kba
        this.previewCheck(uri)
      })
    })
  }

  /* istanbul ignore next */
  combineColumnStack(book: Book, column: Column) {
    let combBittings = []
    let error = false

    book.pages.forEach((page: Page) => {
      page.columns[column.number].blocks.forEach((block) => {
        block.kbas.forEach((kba) => {
          let uri = kba
          if (this.combineCheck(uri)) {
            combBittings.push(uri.masterKeyBitting)
          } else {
            error = true
          }
        })
      })
    })

    if (!error) {
      let request = [
        {
          CombineType: 9,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewCombineColumnStack(book: Book, column: Column) {
    book.pages.forEach((page: Page) => {
      page.columns[column.number].blocks.forEach((block) => {
        block.kbas.forEach((kba) => {
          let uri = kba
          this.previewCheck(uri)
        })
      })
    })
  }

  /* istanbul ignore next */
  combineBlockRowStack(book: Book, column: Column, block: Block) {
    let combBittings = []
    let error = false

    book.pages.forEach((page: Page) => {
      page.columns.forEach((column) => {
        column.blocks[block.number].kbas.forEach((kba) => {
          let uri = kba
          if (this.combineCheck(uri)) {
            combBittings.push(uri.masterKeyBitting)
          } else {
            error = true
          }
        })
      })
    })

    if (!error) {
      let request = [
        {
          CombineType: 10,
          Bittings: combBittings,
        },
      ]
      this.combine(request)
    } else {
      return this.errorService.throw({ error: { message: this.combineError } })
    }
  }

  /* istanbul ignore next */
  previewCombineBlockRowStack(book: Book, column: Column, block: Block) {
    book.pages.forEach((page: Page) => {
      page.columns.forEach((column) => {
        column.blocks[block.number].kbas.forEach((kba) => {
          let uri = kba
          this.previewCheck(uri)
        })
      })
    })
  }

  canCombinePage(page: Page) {
    let issues = false
    page.columns.forEach((column) => {
      if (!this.canCombineColumn(column)) {
        issues = true
      }
    })
    if (page.columns.length !== this.noOfBlocks || issues) {
      return false
    }
    return true
  }

  canCombineColumn(column: Column) {
    let issues = false
    column.blocks.forEach((block) => {
      if (!this.canCombineBlock(block)) {
        issues = true
      }
    })
    if (column.blocks.length !== this.noOfBlocks || issues) {
      return false
    }
    return true
  }

  canCombineBlock(block: Block) {
    if (
      block.kbas.some(
        (bitting) => bitting.assignment && (bitting.assignment.masterKey || bitting?.assignment?.reservationType?.id)
      ) ||
      block.kbas.some((bitting) => bitting.isASplit && !bitting.hidden) ||
      block.kbas.some((bitting) => bitting.isCombine)
    ) {
      return false
    } else {
      return true
    }
  }

  canSeparateBlock(block: Block) {
    if (
      !block.kbas.some((bitting) => bitting.hidden === false) ||
      block.kbas.some(
        (bitting) => bitting.assignment && (bitting.assignment.masterKey || bitting?.assignment?.reservationType?.id)
      )
    ) {
      return false
    }
    return true
  }

  canSeparateColumn(column: Column) {
    if (
      !column.blocks[0].kbas.some((bitting) => bitting.hidden === false) ||
      column.blocks[0].kbas.some(
        (bitting) => bitting.assignment && (bitting.assignment.masterKey || bitting?.assignment?.reservationType?.id)
      )
    ) {
      return false
    }
    return true
  }

  canSeparatePage(page: Page) {
    if (
      !page.columns[0].blocks[0].kbas.some((bitting) => bitting.hidden === false) ||
      page.columns[0].blocks[0].kbas.some(
        (bitting) => bitting.assignment && (bitting.assignment.masterKey || bitting?.assignment?.reservationType?.id)
      )
    ) {
      return false
    }
    return true
  }

  previewCheck(uri: KeyBitArray) {
    if (
      !uri?.assignment?.masterKey &&
      !uri?.assignment?.reservationType?.id &&
      !uri?.children &&
      !uri?.isASplit &&
      !uri?.isCombine
    ) {
      //if (!uri?.hidden) {
      uri.willCombine = !uri.willCombine
      //}
    } else {
      // if (!uri?.hidden || uri?.isCombine) {
      //   uri.error = !uri.error
      // }

      if (uri?.isCombine) {
        uri.error = !uri.error
      }
    }
  }

  combineCheck(uri: KeyBitArray) {
    if (
      !uri?.assignment?.masterKey &&
      !uri?.assignment?.reservationType?.id &&
      !uri?.children &&
      !uri?.isASplit &&
      !uri?.isCombine
    ) {
      //if (!uri?.hidden) {
      return true
      //}
    } else {
      // if (!uri?.hidden || uri?.isCombine) {
      //   return false
      // }

      if (uri?.isCombine) {
        return false
      }
    }
  }

  canSplit(kba: KeyBitArray) {
    if (
      !kba.children.some((bitting) => bitting.hidden === false) ||
      kba.children.some(
        (bitting) => bitting.assignment && (bitting.assignment.masterKey || bitting?.assignment?.reservationType?.id)
      )
    ) {
      return false
    }
    return true
  }

  /* istanbul ignore next */
  previewSplit(kba: KeyBitArray) {
    kba.willSplit = !kba.willSplit
  }

  showControlKeysMaster(selectedMasterKey: MasterKey): boolean {
    if (selectedMasterKey.parent) return this.isControlKeysMasterAssignedBitting

    for (const masterKey of selectedMasterKey.children) {
      if (masterKey.masterKeyTypeId === this.keyNodeType.ControlKeyMaster) {
        if (masterKey.isAssigned && !masterKey.unassignedLocally) {
          this.isControlKeysMasterAssignedBitting = false
        } else {
          this.isControlKeysMasterAssignedBitting = true
        }
      }
    }

    return this.isControlKeysMasterAssignedBitting
  }

  /* istanbul ignore next */
  isSelectedForMove(kba: KeyBitArray): boolean {
    return this.selectedForMoveBittings.some(
      (item) => item.masterKeyBitting === kba.masterKeyBitting
    )
  }

  canMoveKba(kba: KeyBitArray): boolean {
    // All bittings can be moved - reserved, assigned, or empty
    return true
  }

  addToMoveBittingsIfEligible(kba: KeyBitArray): void {
    if (this.canMoveKba(kba) && !this.isSelectedForMove(kba)) {
      this.selectedForMoveBittings.push(kba)
    }
  }

  removeFromMoveBittings(kba: KeyBitArray): void {
    const index = this.selectedForMoveBittings.findIndex(
      (item) => item.masterKeyBitting === kba.masterKeyBitting
    )
    if (index > -1) {
      this.selectedForMoveBittings.splice(index, 1)
    }
  }
}
